import { ApiService } from '@/services';
import {
  JwtDTO,
  Jwt,
  JoinDTO,
  ResetDTO,
  RegisterDTO,
  Response,
  RegisterResponse,
  SubscribeDTO
} from '@/types';
import gql from 'graphql-tag';
import { UtilService } from '@/services';
import Config from '@/config';
import store from '@/store';
import { USER_LOGIN } from '@/store/mutypes';
import { Message } from 'element-ui';

export class AuthService extends ApiService {
  public async login(email: string, password: string): Promise<any> {
    const mutation = gql`
      mutation login($input: LoginInput) {
        login(input: $input) {
          status
          token
          exp
          message
          user {
            id
            name
            email
            status
            created_at
            updated_at
            roles {
              id
              name
            }
            permissions {
              id
              name
            }
          }
        }
      }
    `;
    return this.pubMutate(
      { mutation, variables: { input: { email, password } } },
      'login'
    );
  }

  public async register(registerDTO: RegisterDTO): Promise<RegisterResponse> {
    const mutation = gql`
      mutation register(
        $name: String!
        $email: String!
        $subdomain: String!
        $company: String!
        $website: String!
      ) {
        register(
          name: $name
          email: $email
          subdomain: $subdomain
          company: $company
          website: $website
        ) {
          status
          message
          payment_token
        }
      }
    `;

    return this.pubMutate(
      { mutation, variables: { ...registerDTO } },
      'register'
    );
  }

  public async subscribe(subscribeDTO: SubscribeDTO): Promise<Response> {
    const mutation = gql`
      mutation subscribe(
        $payment_token: String!
        $plan_id: String!
        $stripe_token_id: String!
        $subdomain: String!
      ) {
        subscribe(
          payment_token: $payment_token
          plan_id: $plan_id
          stripe_token_id: $stripe_token_id
          subdomain: $subdomain
        ) {
          status
          message
        }
      }
    `;

    return this.pubMutate(
      { mutation, variables: { ...subscribeDTO } },
      'subscribe'
    );
  }

  public async join(joinDTO: JoinDTO): Promise<JwtDTO> {
    const mutation = gql`
      mutation join($email: String!, $password: String!, $token: String!) {
        join(email: $email, password: $password, token: $token) {
          status
          token
          exp
          message
          user {
            id
            name
            email
            status
            created_at
            updated_at
            roles {
              id
              name
            }
            permissions {
              id
              name
            }
          }
        }
      }
    `;

    return await this.pubMutate({ mutation, variables: joinDTO }, 'join');
  }

  public async reset(resetDTO: ResetDTO): Promise<JwtDTO> {
    const mutation = gql`
      mutation resetPassword(
        $email: String!
        $password: String!
        $token: String!
      ) {
        resetPassword(email: $email, password: $password, token: $token) {
          status
          token
          exp
          message
          user {
            id
            name
            email
            status
            roles {
              id
              name
            }
            permissions {
              id
              name
            }
          }
        }
      }
    `;

    return await this.pubMutate(
      { mutation, variables: resetDTO },
      'resetPassword'
    );
  }

  public async refresh(): Promise<any> {
    try {
      const mutation = gql`
        mutation refreshToken {
          refreshToken {
            status
            token
            exp
            message
            user {
              id
              name
              email
              status
              created_at
              updated_at
              roles {
                id
                name
              }
              permissions {
                id
                name
              }
            }
          }
        }
      `;
      return this.mutate({ mutation }, 'refreshToken');
    } catch (e) {
      store.dispatch('logout');
    }
  }

  public validateJwt(jwt: Jwt): boolean {
    let isValid = true;
    if (jwt && jwt.token && jwt.exp) {
      const timeRemaining = jwt.exp - UtilService.timestamp();
      if (timeRemaining > 1) {
        if (timeRemaining < Config.JWT_TOKEN_REFRESH_TIME) {
          // refresh should not block program execution.
          this.refresh().then(resp => {
            store.commit(USER_LOGIN, resp);
          });
        }
      } else {
        Message.info('Please login again due to inactivity');
        isValid = false;
      }
    } else {
      Message.info('Detected invalid login, Please login again.');
      isValid = false;
    }
    return isValid;
  }
}

export const authSvc: AuthService = new AuthService();
